import { useUserSession } from '@solinkcorp/authentication';
import { useCallhomeClient } from '@solinkcorp/callhome-data';
import { useMemo } from 'react';
import useSWR from 'swr';
import useDefaultAiTags from './useDefaultAiTags';
import { AI_TAG_PLACEHOLDERS_KEY, AI_TAGS_KEY, aiTagIdFromName, CUSTOM_CATEGORY } from './utils';

/**
 * Plain-string shape of a persisted AI tag. Mirrors the runtime payload
 * returned by `callhomeClient.getAiTag` (callhome-data uses plain strings
 * rather than the branded ids on `AiTagDto`), so this is what flows through
 * the SWR cache for both real and placeholder tags.
 */
export interface AiTag {
  id: string;
  cloudId: string;
  resellerId: string;
  customerId: string;
  name: string;
}

export interface AiTagWithCategory extends AiTag {
  category: string;
}

/**
 * Raw customer-saved tags from /api/ai-tags. The authoritative set of "tags
 * the customer has actually persisted" — use this in useSaveAiTags to decide
 * which selected ids still need to be POSTed.
 *
 * Shares the SWR cache key with useAiTags so calling both does not duplicate
 * the request.
 */
export function useCustomerAiTags() {
  const user = useUserSession();
  const callhomeClient = useCallhomeClient();
  return useSWR(AI_TAGS_KEY, () =>
    callhomeClient.getAiTag({ where: { customerId: user.customerId } }),
  );
}

/**
 * Customer-saved tags merged with Solink default tags from clientSettings,
 * plus any freshly-typed placeholders seeded via `addPlaceholderToCache`.
 * Every entry carries a `category`: customer tags inherit their category from
 * a matching default if one exists by name, otherwise fall into `Custom`.
 * Defaults whose name is already saved as a customer tag are dropped (customer
 * record wins so its real BE id is preserved). Placeholders are appended under
 * `Custom` and dropped once a matching persisted customer tag shows up.
 */
export default function useAiTags() {
  const user = useUserSession();
  const swr = useCustomerAiTags();
  const defaults = useDefaultAiTags();
  const { data: placeholders } = useSWR<AiTag[]>(AI_TAG_PLACEHOLDERS_KEY, null);

  const data = useMemo<AiTagWithCategory[]>(() => {
    const defaultCategoryByName = new Map(
      defaults.map((d) => [d.name.toLowerCase(), d.category] as const),
    );

    const customerTags: AiTagWithCategory[] = (swr.data ?? []).map((t) => ({
      ...t,
      category: defaultCategoryByName.get(t.name.toLowerCase()) ?? CUSTOM_CATEGORY,
    }));

    const customerNames = new Set(customerTags.map((t) => t.name.toLowerCase()));
    const customerIds = new Set(customerTags.map((t) => t.id));

    const newDefaults: AiTagWithCategory[] = defaults
      .filter((d) => !customerNames.has(d.name.toLowerCase()))
      .map((d) => ({
        id: aiTagIdFromName(d.name, user.customerId),
        name: d.name,
        category: d.category,
        cloudId: user.jwtUser.app_metadata.cloudId,
        resellerId: user.jwtUser.app_metadata.resellerId,
        customerId: user.customerId,
      }));

    const placeholderTags: AiTagWithCategory[] = (placeholders ?? [])
      .filter(
        (p) =>
          p.customerId === user.customerId &&
          !customerIds.has(p.id) &&
          !customerNames.has(p.name.toLowerCase()),
      )
      .map((p) => ({ ...p, category: CUSTOM_CATEGORY }));

    return [...customerTags, ...newDefaults, ...placeholderTags];
  }, [
    swr.data,
    defaults,
    placeholders,
    user.customerId,
    user.jwtUser.app_metadata.cloudId,
    user.jwtUser.app_metadata.resellerId,
  ]);

  return { ...swr, data };
}
